/*
 * ts-common is the basic building blocks of our typescript projects
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Logger } from '../core/logger/index.js';
import { ValidationException } from '../validator/validator-core.js';
/**
 * String replacement utility with parameter substitution and loop support.
 *
 * Provides template string processing with:
 * - Parameter substitution: `${param}` or `${${param}}` (nested)
 * - For-each loops: `{{foreach item in array}}...{{/foreach}}`
 * - Fallback replacer support (chain of replacers)
 * - Strict mode (throws on missing params) or non-strict mode
 * - Recursive replacement (processes replacements in replacement results)
 *
 * **Parameter syntax**: `${param}` or `${${nested}}` for nested parameter access
 * **Loop syntax**: `{{foreach item in array}}...content...{{/foreach}}`
 */
export class Replacer extends Logger {
    /** Whether to throw errors on missing parameters (default: true) */
    strictMode = true;
    /** Runtime parameter key */
    static RuntimeParam = '__runtime';
    /** Indicator prefix for runtime parameters */
    static Indicator_RuntimeParam = '__';
    /** Regex for matching all parameter groups */
    static Regexp_paramGroup = /\$\{(\{?.*?\}?)\}/g;
    /** Regex for matching a single parameter */
    static Regexp_param = /\$\{(\{?.*?\}?)\}/;
    /** Regex for matching for-each loop start */
    static Regexp_forLoopGroupStart = /\{\{foreach (.*?) in (.*?)\}\}/g;
    /** Regex for matching for-each loop parameters */
    static Regexp_forLoopParam = /\{\{foreach (.*?) in (.*?)\}\}/;
    /** Input object containing parameter values */
    input = {};
    /** Aliases for parameter names */
    aliases = [];
    /** Optional fallback replacer to use when parameter is missing */
    fallbackReplacer;
    constructor() {
        super();
    }
    setFallbackReplacer(fallbackReplacer) {
        this.fallbackReplacer = fallbackReplacer;
        return this;
    }
    setNotStrict() {
        this.strictMode = false;
        return this;
    }
    getInput() {
        return this.input;
    }
    setInput(input, aliases = []) {
        this.input = input;
        this.aliases = aliases;
        return this;
    }
    /**
     * Replaces parameters and loops in content recursively.
     *
     * Processes loops first, then parameters. If any replacements were made,
     * recursively processes again to handle nested replacements.
     *
     * @param _content - Template content to process
     * @param runtime - Optional runtime parameters (merged with input)
     * @returns Processed content with all replacements applied
     */
    replace(_content = '', runtime) {
        let content = this.replaceLoops(_content, runtime);
        content = this.replaceParams(content, runtime);
        if (_content !== content)
            content = this.replace(content, runtime);
        return content;
    }
    replaceParams(content = '', runtime) {
        const matches = content.match(Replacer.Regexp_paramGroup);
        return matches?.reduce((toRet, match) => {
            let param = match;
            while (Replacer.Regexp_param.test(param))
                param = param.match(Replacer.Regexp_param)[1];
            if (param === undefined)
                return toRet;
            const value = this.resolveParam(param, toRet, runtime);
            return toRet.replace(match, value);
        }, content) || content;
    }
    resolveParam(param, toRet, runtime) {
        const value = this.resolveParamValue(param, runtime);
        if (this.fallbackReplacer && (value === undefined || value === ''))
            return this.fallbackReplacer.resolveParam(param, toRet, runtime);
        return value;
    }
    replaceLoops(content = '', runtime) {
        const matches = content.match(Replacer.Regexp_forLoopGroupStart);
        return matches?.reduce((toRet, match) => {
            const varsMatch = match.match(Replacer.Regexp_forLoopParam);
            const string = varsMatch[0];
            const iterable = varsMatch[1];
            const iterator = varsMatch[2];
            const endMatch = `{{/foreach ${iterator}}}`;
            const indexOfEnd = toRet.indexOf(endMatch);
            const fullOriginLoopText = toRet.substring(toRet.indexOf(string), indexOfEnd + endMatch.length);
            const loopText = toRet.substring(toRet.indexOf(string) + string.length, indexOfEnd);
            this.logDebug(`indexOfEnd: ${endMatch} ${indexOfEnd}`);
            this.logDebug(`string: ${string} ${toRet.indexOf(string)}`);
            this.logDebug(`iterable: ${iterable}`);
            this.logDebug(`iterator: ${iterator}`);
            this.logDebug(`loopText: ${loopText}`);
            let loopArray;
            const iterableProp = `${iterator}`;
            try {
                loopArray = this.resolveParamValue(iterableProp, runtime);
            }
            catch (e) {
                throw new ValidationException(`Error while looping on variable for parts: `, iterableProp, JSON.stringify(this.input), e);
            }
            if (!Array.isArray(loopArray))
                this.logWarning(`Loop object is not an array.. found:`, loopArray);
            const replacement = loopArray.reduce((_toRet, value) => {
                return _toRet + this.replace(loopText, { property: value });
            }, '');
            const s = toRet.replace(fullOriginLoopText, replacement);
            return s;
        }, content) || content;
    }
    resolveParamValue(_param, runtime) {
        let param = _param;
        const alias = this.aliases.find(alias => alias.key === param);
        if (alias) {
            this.logInfo('using alias: ', alias);
            param = alias.value || param;
        }
        param = param.replace(/\[/g, '.').replace(/\]/g, '');
        if (param.endsWith('.'))
            param = param.substring(0, param.length - 1);
        if (param.startsWith(Replacer.Indicator_RuntimeParam))
            param = `${Replacer.RuntimeParam}.${param.substring(Replacer.Indicator_RuntimeParam.length)}`;
        const parts = param.split('\.');
        let value;
        try {
            if (runtime)
                value = parts.reduce((value, key) => value?.[key], runtime);
            if (value === undefined)
                value = parts.reduce((value, key) => value[key], this.input);
        }
        catch (e) {
            this.logWarning('input', this.input);
            throw new ValidationException(`Error while resolving runtime variable for parts ${param}`, param, JSON.stringify(this.input), e);
        }
        if (value === undefined) {
            this.logWarning('input', this.input);
            if (this.strictMode)
                throw new ValidationException(`Cannot resolve runtime variable for parts ${param}`, param, JSON.stringify(this.input));
        }
        return value;
        // this.logDebug(`Input:`, this.input);
        // this.logDebug(`Param: ${param}`, 'Parts: ', parts);
        // this.logDebug(value);
    }
}
//# sourceMappingURL=Replacer.js.map